import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Percent, Save, CreditCard, Receipt, FileSpreadsheet, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PROVIDER_LABELS = { stitch: "Stitch", yoco: "Yoco", paystack: "Paystack", scantopay: "Scan to Pay" };
const PROCESSOR_TONES = {
  Stitch: "bg-brand-green/10 text-brand-green",
  Yoco: "bg-brand-orange/10 text-brand-orange",
  Paystack: "bg-sky-500/10 text-sky-600",
  ScanToPay: "bg-violet-500/10 text-violet-600",
};

function ProcessorBadge({ name }) {
  const tone = PROCESSOR_TONES[name] || "bg-secondary text-muted-foreground";
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`} data-testid="txn-processor-badge">
      {name || "Unknown"}
    </span>
  );
}

function Card({ label, value, icon: Icon, tone }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between"><p className="overline-label text-muted-foreground">{label}</p><Icon className={`h-5 w-5 ${tone}`} /></div>
      <p className="mt-3 font-heading text-2xl font-extrabold">{value}</p>
    </div>
  );
}

export default function AdminFinance() {
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [settings, setSettings] = useState(null);
  const [txns, setTxns] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [fProcessor, setFProcessor] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [exporting, setExporting] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => {
    api.get("/admin/reconcile").then((r) => setRec(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const loadTxns = () => {
    const params = new URLSearchParams();
    if (fProcessor) params.set("processor", fProcessor);
    if (fStatus) params.set("status", fStatus);
    api.get(`/admin/transactions?${params.toString()}`).then((r) => {
      setTxns(r.data.transactions || []);
      if (r.data.processors) setProcessors(r.data.processors);
    }).catch(() => {});
  };
  useEffect(() => { loadTxns(); }, [fProcessor, fStatus]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (fProcessor) params.set("processor", fProcessor);
      if (fStatus) params.set("status", fStatus);
      const res = await api.get(`/admin/transactions/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tiplyme-transactions-${(fProcessor || "all").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
    finally { setExporting(false); }
  };

  const save = async () => {
    try {
      await api.patch("/admin/settings", {
        cashout_rate_pct: Number(settings.cashout_rate_pct),
        eft_fee: Number(settings.eft_fee),
        min_cashout: Number(settings.min_cashout),
        cash_in_fee_pct: Number(settings.cash_in_fee_pct),
        payment_provider: settings.payment_provider || "stitch",
      });
      toast.success("Finance settings saved");
    } catch (err) { toast.error(apiError(err)); }
  };

  const setProvider = async (provider) => {
    setSettings({ ...settings, payment_provider: provider });
    try {
      await api.patch("/admin/settings", { payment_provider: provider });
      toast.success(`Payments now routing via ${PROVIDER_LABELS[provider] || provider}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!rec || !settings) return null;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-finance">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Finance &amp; reconciliation</h1>
        <p className="mt-1 text-muted-foreground">Match incoming tips against outgoing cashouts.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Gross inflow" value={`R${(rec.gross_inflow ?? rec.total_inflow).toFixed(2)}`} icon={ArrowDownLeft} tone="text-brand-greenLight" />
        <Card label={`Merchant fees (${(rec.cash_in_fee_pct ?? 3.2)}%)`} value={`R${(rec.merchant_fees ?? 0).toFixed(2)}`} icon={Percent} tone="text-brand-orange" />
        <Card label="Net inflow" value={`R${(rec.net_inflow ?? rec.total_inflow).toFixed(2)}`} icon={ArrowDownLeft} tone="text-brand-greenLight" />
        <Card label="Cashed out" value={`R${rec.total_cashed_out.toFixed(2)}`} icon={ArrowUpRight} tone="text-brand-orange" />
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm" data-testid="payment-provider-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-brand-green" />
            <h2 className="font-heading text-lg font-bold">Payment provider {isAdmin ? "" : "(read-only)"}</h2>
          </div>
          <span className="rounded-full bg-brand-green/10 px-3 py-1 text-xs font-semibold text-brand-green" data-testid="active-provider-badge">
            Active: {PROVIDER_LABELS[settings.payment_provider || "stitch"]}
          </span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Stitch is the primary gateway. Switch to Yoco, Paystack or Scan to Pay as a failover — all new payments route via the selected provider. Scan to Pay is QR-native: tippers pay from their own SA bank app.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { key: "stitch", label: "Stitch", sub: "Primary" },
            { key: "yoco", label: "Yoco", sub: "Failover" },
            { key: "paystack", label: "Paystack", sub: "Failover" },
            { key: "scantopay", label: "Scan to Pay", sub: "QR · bank app" },
          ].map((p) => {
            const active = (settings.payment_provider || "stitch") === p.key;
            return (
              <button
                key={p.key}
                disabled={!isAdmin}
                onClick={() => setProvider(p.key)}
                data-testid={`provider-${p.key}-button`}
                className={`rounded-2xl border px-4 py-4 text-left transition-all disabled:opacity-60 ${active ? "border-brand-green bg-brand-green/10 shadow-sm ring-1 ring-brand-green/30" : "border-border bg-background hover:border-brand-green/40"}`}>
                <p className="font-heading text-lg font-bold text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground">{p.sub}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <h2 className="font-heading text-lg font-bold">Inflow by processor</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(rec.by_processor).length === 0 && <p className="text-sm text-muted-foreground">No inflow yet.</p>}
            {Object.entries(rec.by_processor).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm"><span className="font-semibold">{k}</span><span>R{v.toFixed(2)}</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-brand-green" style={{ width: `${rec.total_inflow ? (v / rec.total_inflow) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
            <div className="mt-2 flex justify-between border-t border-border/60 pt-3 text-sm"><span className="font-semibold">Fees collected</span><span className="font-bold text-brand-greenLight">R{rec.fees_collected.toFixed(2)}</span></div>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
          <h2 className="font-heading text-lg font-bold">Fees &amp; rates {isAdmin ? "" : "(read-only)"}</h2>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Merchant cash-in fee (%)</Label>
              <Input type="number" step="0.01" disabled={!isAdmin} value={settings.cash_in_fee_pct ?? 3.2} onChange={(e) => setSettings({ ...settings, cash_in_fee_pct: e.target.value })} data-testid="cashin-fee-input" className="mt-1" />
              <p className="mt-1 text-xs text-muted-foreground">Deducted from every incoming tip and shown separately to workers.</p>
            </div>
            <div><Label>Cashout rate (%)</Label><Input type="number" step="0.1" disabled={!isAdmin} value={settings.cashout_rate_pct} onChange={(e) => setSettings({ ...settings, cashout_rate_pct: e.target.value })} data-testid="rate-input" className="mt-1" /></div>
            <div><Label>Flat EFT fee (R)</Label><Input type="number" step="0.5" disabled={!isAdmin} value={settings.eft_fee} onChange={(e) => setSettings({ ...settings, eft_fee: e.target.value })} data-testid="eft-fee-input" className="mt-1" /></div>
            <div><Label>Minimum cashout (R)</Label><Input type="number" disabled={!isAdmin} value={settings.min_cashout} onChange={(e) => setSettings({ ...settings, min_cashout: e.target.value })} data-testid="min-cashout-input" className="mt-1" /></div>
            {isAdmin && <Button onClick={save} data-testid="save-settings-button" className="w-full gap-2 rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90"><Save className="h-4 w-4" /> Save settings</Button>}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm" data-testid="transactions-by-processor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand-green" />
            <h2 className="font-heading text-lg font-bold">Transactions by processor</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={fProcessor}
              onChange={(e) => setFProcessor(e.target.value)}
              data-testid="filter-processor-select"
              className="rounded-full border border-border bg-background px-3 py-1.5 text-sm">
              <option value="">All processors</option>
              {processors.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
              data-testid="filter-status-select"
              className="rounded-full border border-border bg-background px-3 py-1.5 text-sm">
              <option value="">All statuses</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="export-transactions-csv" className="gap-1.5 rounded-full">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Export CSV
            </Button>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Every tip is tagged with the payment processor that handled it. Filter and export a clean statement per processor.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left text-xs uppercase text-muted-foreground">
                <th className="pb-2 pr-3 font-semibold">Reference</th>
                <th className="pb-2 pr-3 font-semibold">Processor</th>
                <th className="pb-2 pr-3 font-semibold">Worker</th>
                <th className="pb-2 pr-3 font-semibold">Amount</th>
                <th className="pb-2 pr-3 font-semibold">Status</th>
                <th className="pb-2 font-semibold">Processor ref</th>
              </tr>
            </thead>
            <tbody data-testid="transactions-table-body">
              {txns.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No transactions yet.</td></tr>
              )}
              {txns.map((t) => (
                <tr key={t.id} className="border-b border-border/40 last:border-0" data-testid={`txn-row-${t.id}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs">{t.reference || "—"}</td>
                  <td className="py-2.5 pr-3"><ProcessorBadge name={t.processor} /></td>
                  <td className="py-2.5 pr-3">{t.worker_name || "—"}</td>
                  <td className="py-2.5 pr-3 font-semibold">R{Number(t.amount).toFixed(2)}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-xs font-semibold ${t.status === "success" ? "text-brand-green" : t.status === "failed" ? "text-red-500" : "text-brand-orange"}`}>{t.status}</span>
                  </td>
                  <td className="py-2.5 font-mono text-xs text-muted-foreground">{t.processor_ref || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
